import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { Clear, Delete, Save } from '@mui/icons-material';

const SavedDataGridViewsToolbar = ({
  views,
  selectedViewId,
  currentUserId,
  isDirty,
  isOpen,
  viewToDelete,
  onOpen,
  onOpenChange,
  onViewToDeleteChange,
  onSelect,
  onSave,
  onDelete,
}) => {
  const [isNameDialogOpen, setIsNameDialogOpen] = useState(false);
  const [name, setName] = useState('');

  const selectedView = views.find((view) => view.id === selectedViewId);
  const isOwner = selectedView?.authorUserId === currentUserId;

  const openSaveDialog = () => {
    setName(selectedView ? `${selectedView.name} - másolat` : '');
    setIsNameDialogOpen(true);
  };

  const handleSave = () => {
    if (selectedView && isOwner) {
      onSave();
      return;
    }
    openSaveDialog();
  };

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    await onSave(trimmedName);
    setIsNameDialogOpen(false);
    setName('');
  };

  const renderView = (view) => (
    <MenuItem key={view.id} value={view.id}>
      <Tooltip title={`Tulajdonos: ${view.authorName}`} placement="left">
        <Box component="span" sx={{ flexGrow: 1 }}>
          {view.name}
          {view.id === selectedViewId && isDirty ? ' *' : ''}
        </Box>
      </Tooltip>
      {view.authorUserId === currentUserId && (
        <IconButton
          size="small"
          aria-label="Nézet törlése"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onOpenChange(false);
            onViewToDeleteChange(view);
          }}
          sx={{ color: 'primary.main' }}
        >
          <Delete fontSize="small" />
        </IconButton>
      )}
    </MenuItem>
  );

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginLeft: 'auto' }}>
        <FormControl variant="standard" sx={{ minWidth: 220 }}>
          <Select
            value={selectedViewId ?? ''}
            onChange={(event) => onSelect(event.target.value === '' ? null : Number(event.target.value))}
            open={isOpen}
            onOpen={() => {
              onOpenChange(true);
              onOpen();
            }}
            onClose={() => onOpenChange(false)}
            displayEmpty
            aria-label="Mentett nézet"
            endAdornment={
              selectedViewId !== null && selectedViewId !== undefined ? (
                <IconButton
                  size="small"
                  aria-label="Kiválasztott nézet ürítése"
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelect(null);
                  }}
                  sx={{ marginRight: 2, color: 'primary.main' }}
                >
                  <Clear fontSize="small" />
                </IconButton>
              ) : null
            }
            MenuProps={{
              PaperProps: {
                sx: {
                  '& .MuiMenuItem-root': { color: 'primary.main' },
                },
              },
            }}
            sx={{
              color: 'primary.main',
              '& .MuiSelect-icon': { color: 'primary.main' },
            }}
          >
            <MenuItem value="">Alapértelmezett</MenuItem>
            {views.map(renderView)}
          </Select>
        </FormControl>
        <Tooltip
          title={
            selectedView && isOwner ? 'Nézet frissítése' : selectedView ? 'Mentés másolatként' : 'Új nézet mentése'
          }
        >
          <span>
            <IconButton
              aria-label="Nézet mentése"
              color="primary"
              onClick={handleSave}
              disabled={Boolean(selectedView && isOwner && !isDirty)}
            >
              <Save />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Dialog open={isNameDialogOpen} onClose={() => setIsNameDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{selectedView ? 'Nézet mentése másolatként' : 'Új nézet mentése'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Nézet neve"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleCreate();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsNameDialogOpen(false)}>Mégsem</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!name.trim()}>
            Mentés
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(viewToDelete)} onClose={() => onViewToDeleteChange(null)}>
        <DialogTitle>Nézet törlése</DialogTitle>
        <DialogContent>
          <DialogContentText>Biztosan törölni szeretné a(z) „{viewToDelete?.name}” nézetet?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onViewToDeleteChange(null)}>Mégsem</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              await onDelete(viewToDelete.id);
              onViewToDeleteChange(null);
            }}
          >
            Törlés
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SavedDataGridViewsToolbar;
